import React, { useEffect, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';
import DynamicPracticePage from '../../components/common/DynamicPracticePage';
import { TopicItem } from '../../components/common/PracticeContentLayout';
import { ContentPage } from '../../types/content';
import { loadHLDProblemContent } from '../../data/hld/hldProblemContent';

const topics: TopicItem[] = [{
  id: 'rate-limiter-resources',
  title: 'Rate Limiter',
  derivedCompletion: true,
  children: [
    { id: 'rate-limiter', title: 'Learn the problem' },
    {
      id: 'rate-limiter-workspace',
      title: 'Design the system',
      path: '/practice/hld/problems/rate-limiter/workspace',
      trackProgress: false,
    },
  ],
}, {
  id: 'consistent-hashing-resources',
  title: 'Consistent Hashing',
  derivedCompletion: true,
  children: [
    { id: 'consistent-hashing', title: 'Learn the problem' },
    {
      id: 'consistent-hashing-workspace',
      title: 'Design the system',
      path: '/practice/hld/problems/consistent-hashing/workspace',
      trackProgress: false,
    },
  ],
}, {
  id: 'key-value-store-resources',
  title: 'Key-Value Store',
  derivedCompletion: true,
  children: [
    { id: 'key-value-store', title: 'Learn the problem' },
    {
      id: 'key-value-store-workspace',
      title: 'Design the system',
      path: '/practice/hld/problems/key-value-store/workspace',
      trackProgress: false,
    },
  ],
}, {
  id: 'unique-id-generator-resources',
  title: 'Unique ID Generator',
  derivedCompletion: true,
  children: [
    { id: 'unique-id-generator', title: 'Learn the problem' },
    {
      id: 'unique-id-generator-workspace',
      title: 'Design the system',
      path: '/practice/hld/problems/unique-id-generator/workspace',
      trackProgress: false,
    },
  ],
}, {
  id: 'url-shortener-resources',
  title: 'URL Shortener',
  derivedCompletion: true,
  children: [
    { id: 'url-shortener', title: 'Learn the problem' },
    {
      id: 'url-shortener-workspace',
      title: 'Design the system',
      path: '/practice/hld/problems/url-shortener/workspace',
      trackProgress: false,
    },
  ],
}, {
  id: 'web-crawler-resources',
  title: 'Web Crawler',
  derivedCompletion: true,
  children: [
    { id: 'web-crawler', title: 'Learn the problem' },
    {
      id: 'web-crawler-workspace',
      title: 'Design the system',
      path: '/practice/hld/problems/web-crawler/workspace',
      trackProgress: false,
    },
  ],
}, {
  id: 'notification-system-resources',
  title: 'Notification System',
  derivedCompletion: true,
  children: [
    { id: 'notification-system', title: 'Learn the problem' },
    { id: 'notification-system-workspace', title: 'Design the system', path: '/practice/hld/problems/notification-system/workspace', trackProgress: false },
  ],
}, {
  id: 'news-feed-system-resources',
  title: 'News Feed System',
  derivedCompletion: true,
  children: [
    { id: 'news-feed-system', title: 'Learn the problem' },
    { id: 'news-feed-system-workspace', title: 'Design the system', path: '/practice/hld/problems/news-feed-system/workspace', trackProgress: false },
  ],
}, {
  id: 'chat-system-resources',
  title: 'Chat System',
  derivedCompletion: true,
  children: [
    { id: 'chat-system', title: 'Learn the problem' },
    { id: 'chat-system-workspace', title: 'Design the system', path: '/practice/hld/problems/chat-system/workspace', trackProgress: false },
  ],
}, {
  id: 'search-autocomplete-resources',
  title: 'Search Autocomplete',
  derivedCompletion: true,
  children: [
    { id: 'search-autocomplete', title: 'Learn the problem' },
    { id: 'search-autocomplete-workspace', title: 'Design the system', path: '/practice/hld/problems/search-autocomplete/workspace', trackProgress: false },
  ],
}, {
  id: 'youtube-resources',
  title: 'YouTube',
  derivedCompletion: true,
  children: [
    { id: 'youtube', title: 'Learn the problem' },
    { id: 'youtube-workspace', title: 'Design the system', path: '/practice/hld/problems/youtube/workspace', trackProgress: false },
  ],
}, {
  id: 'google-drive-resources',
  title: 'Google Drive',
  derivedCompletion: true,
  children: [
    { id: 'google-drive', title: 'Learn the problem' },
    { id: 'google-drive-workspace', title: 'Design the system', path: '/practice/hld/problems/google-drive/workspace', trackProgress: false },
  ],
}];

const HLDProblemArticle: React.FC = () => {
  const { topicId = 'rate-limiter' } = useParams<{ topicId: string }>();
  const [contentPages, setContentPages] = useState<Record<string, ContentPage> | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHLDProblemContent()
      .then(setContentPages)
      .catch(() => setError('The HLD problem content could not be loaded. Please refresh the page.'));
  }, []);

  if (error) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 text-center">
        <div>
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-red-500" />
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Content unavailable</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!contentPages) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 dark:text-primary-400 mx-auto mb-3" />
          <p className="text-gray-600 dark:text-gray-400">Loading HLD problem…</p>
        </div>
      </div>
    );
  }

  return (
    <DynamicPracticePage
      topicType="hld"
      title="HLD Practice Problems"
      topics={topics}
      basePath="/practice/hld/problems"
      getSlugForTopic={(topicId) => `hld-problem-${topicId}`}
      staticContentPages={contentPages}
      articleClassName="system-design-article-page"
    />
  );
};

export default HLDProblemArticle;
